import { BasicSymbolType } from "../../../dev/reel/symbol/config/IReelSymbolStruct";
import { ArrayHelper } from "../../../dev/utils/ArrayHelper";
import { IWinRatioConfig } from "../../config/base/IWinConfig";
import { IAllWinData, IBetWayWinData, IConnectedWin, IConnectedWinBool, stickyFS } from './IData';

export class DataUtil {


    protected createConnectedNearWinMap(allWin: IAllWinData, reels: string[][], evalReels: string[][], stickies: stickyFS[] = []): boolean[][] {
        const size = reels[0].length - 2;

        const filteredReels = reels.map((r, reel) => r.map((w, row) => {
            if (row == 0 || size < row) return "";
            return w;
        }).filter(v => !!v));

        stickies.forEach(v => filteredReels[v.reel][v.row] = v.type);

        let miniMap = this.minimizedConnectedWays(filteredReels, ["WC1", "WC2", "WC3", "WC12"], size, 2)

        if (miniMap[1].indexOf(true) >= 0) {
            for (let i = 2; i < allWin.longest; i++) {
                miniMap.push(ArrayHelper.initArrayWithValues(size, () => false))
                for (let k = 0; k < size; k++) {
                    if (allWin.reelWinPositions[i][k] && evalReels[i][k] !== "W")
                        miniMap[i][k] = true;
                }
            }
            return miniMap;
        }
        return [];
    }

    protected minimizedConnectedWays(windowReel: string[][], specialSymbols: string[], reelHeight: number = 3, minWinLength: number = 2): boolean[][] {

        let firstSymbols: string[][] = ArrayHelper.initArrayWithValues(reelHeight, (i) => [windowReel[0][i]]);
        let res: boolean[][] = ArrayHelper.initArrayWithValues(minWinLength, () => ArrayHelper.initArrayWithValues(reelHeight, (i) => false));

        // Lets chase after potentiall special connections
        for (let specIndx: number = 0; specIndx < firstSymbols.length; specIndx++) {

            if (specialSymbols.find((v) => v === firstSymbols[specIndx][0])) {

                const connected: boolean[][] = [ArrayHelper.initArrayWithValues(firstSymbols.length, (i) => i === specIndx)];

                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], specialSymbols);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (connected.length && connected.length != windowReel.length) connected.push(ArrayHelper.initArrayWithValues(reelHeight, (i) => false))

                let fetchSym: string[] = [];
                for (let conn: number = 1; conn < connected.length; conn++) {
                    connected[conn - 1].forEach((v, i) => {
                        if (v) {
                            connectCheck(i, connected[conn - 1].length, (k) => {
                                if (!connected[conn][k] && ["S", "M"].indexOf(windowReel[conn][k]) === -1) {
                                    fetchSym.push(windowReel[conn][k]);
                                }
                            })
                        }
                    })
                }
                if (fetchSym.length > 1) {
                    firstSymbols[specIndx].push(...fetchSym.filter(onlyUnique))
                } else {
                    firstSymbols[specIndx].push(...fetchSym)
                }
            }
        }

        firstSymbols.forEach((r, indx) => {
            firstSymbols[indx] = r.filter(v => specialSymbols.indexOf(v) === -1)
        });

        for (let startIndx: number = 0; startIndx < firstSymbols.length; startIndx++) {
            for (let secDim: number = 0; secDim < firstSymbols[startIndx].length; secDim++) {
                const toCheck: string = firstSymbols[startIndx][secDim];

                const connected: boolean[][] = [ArrayHelper.initArrayWithValues(firstSymbols.length, (i) => i === startIndx)];

                //Find connections FromStart
                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], [toCheck, ...specialSymbols]);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (connected.length < minWinLength || (connected.length == minWinLength && connected[minWinLength - 1].indexOf(!0) == -1)) { // ignore
                    continue;
                }

                // Remove nonPossibleConnections, less than minWinLength will be removed.
                connected[minWinLength - 2].forEach((v, i) => {
                    if (v) {
                        let isPossible: boolean = false;
                        connectCheck(i, connected[minWinLength - 2].length, (checkIndx) => {
                            if (connected[minWinLength - 1][checkIndx]) isPossible = true;
                        });
                        connected[minWinLength - 2][i] = isPossible;
                    }
                })

                ArrayHelper.eachForEach(connected, (reel, row) => {
                    if (reel < 2 && connected[reel][row]) res[reel][row] = true;
                })
            }
        }

        return res;

        function onlyUnique(value: any, index: any, self: any) {
            return self.indexOf(value) === index;
        }

        function checkConnected(prevConnected: boolean[], reel: string[], symbolType: string[]): boolean[] {

            let minorMap: boolean[] = ArrayHelper.initArrayWithValues(prevConnected.length, () => false);

            prevConnected.forEach((v, i) => {
                if (v) {
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = checker(reel[checkIndx], symbolType);
                    })
                }
            })
            return minorMap;
        }

        function connectCheck(startIndx: number, maxHeight: number, func: (indx: number) => void): void {
            const startPos: number = Math.max(0, startIndx - 1); // Forward-Up
            const endPos: number = Math.min(maxHeight - 1, startIndx + 1); // Forward-Down
            for (let checkIndx: number = startPos; checkIndx <= endPos; checkIndx++) func(checkIndx)
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find(v => v === symbol);
        }

    }

    protected symbolTypePos(symType: string | string[], windowReel: string[][]): number[][] {

        const res: number[][] = [];
        const isArray: boolean = !(typeof symType === 'string');

        for (let r: number = 0; r < windowReel.length; r++) {
            res.push([]);
            for (let c: number = 0; c < windowReel[r].length; c++) {
                if (isArray)
                    res[r].push(symType.indexOf(windowReel[r][c]) >= 0 ? c : -1)
                else
                    res[r].push(windowReel[r][c] === symType ? c : -1);
            }
        }
        return res;
    }

    protected symbolBooleanPos(symType: string | string[], windowReel: string[][]): boolean[][] {
        return this.symbolTypePos(symType, windowReel).map(a => a.map(v => v >= 0));
    }

    protected fetchSymbolPos(symType: string, windowReel: string[][], singleSymPerReel?: boolean): number[][] | number[] {
        const arr: number[][] | [] = [];

        let pos: number = -1;
        for (let reel: number = 0; reel < windowReel.length; reel++) {
            if (!singleSymPerReel) arr[reel] = [];
            else pos = -1;
            for (let row: number = 0; row < windowReel[reel].length; row++) {
                if (windowReel[reel][row] == symType) {
                    if (singleSymPerReel) {
                        pos = row;

                        break;
                    }
                    else arr[reel].push(row)
                }
            }
            if (singleSymPerReel) (arr as number[]).push(pos)
        }
        return arr;
    }

    protected countTotalAmountOfType(symType: string | string[], windowReel: string[][]): number {
        return this.amountOfTypeOnEachReel(symType, windowReel).reduce((a, b) => a + b);
    }

    protected amountOfTypeOnEachReel(symType: string | string[], windowReel: string[][]): number[] {
        let count: number = 0;
        const arr: number[] = [];
        symType = Array.isArray(symType) ? symType : [symType]
        for (let r: number = 0; r < windowReel.length; r++) {
            count = 0;
            for (let row: number = 0; row < windowReel[r].length; row++) {
                if (symType.indexOf(windowReel[r][row]) >= 0) count++;
            }
            arr.push(count);
        }
        return arr;
    }

    protected fetchHitSymbols(posArr: number[][], windowReel: string[][], filter: string[]): string[][] {
        const arr: string[][] = [];
        for (let m: number = 0; m < posArr.length; m++) {
            arr.push([]);
            for (let j: number = 0; j < posArr[m].length; j++) {
                let symTargetName: string = windowReel[m][posArr[m][j]];

                if (arr[m].indexOf(symTargetName) == -1) {
                    arr[m].push(symTargetName);
                    if ([BasicSymbolType.WILD, "WC1", "WC2", "WC3", "WC12"].indexOf(symTargetName) >= 0 && m != 0) {
                        arr[m] = arr[m].concat(arr[m]);
                    }
                }
            }
            arr[m] = arr[m].filter((sym: string) => { return filter.indexOf(sym) === -1; })
        }
        return arr;
    }

    protected countAmountOfWays(symbolSizeMap: number[][]): number {
        var res = symbolSizeMap[0].reduce((a, b) => a + b, 0);
        for (let i = 1; i < symbolSizeMap.length; i++) res *= symbolSizeMap[i].reduce((a, b) => a + b, 0);
        return res;
    }

    protected addBetLvls(data: IBetWayWinData[], betAmount: number, winRatios: IWinRatioConfig[]): IBetWayWinData[] {

        const res: IBetWayWinData[] = [];

        for (let d: number = 0; d < data.length; d++) {
            const betGrp: IBetWayWinData = <IBetWayWinData>data[d];

            let winLvl: number = 0;
            const amountRatio: number = data[d].amount / betAmount;
            winRatios.forEach((v, i) => v.ratio <= amountRatio ? winLvl = i : winLvl)

            betGrp.reelWinLvlPositions = [];
            betGrp.winLvl = winLvl;
            for (let reel: number = 0; reel < data[d].reelWinPositions.length; reel++) {
                betGrp.reelWinLvlPositions[reel] = [];
                for (let row: number = 0; row < data[d].reelWinPositions[reel].length; row++) {
                    betGrp.reelWinLvlPositions[reel][row] = data[d].reelWinPositions[reel][row] ? winLvl : -1;
                }
            }

            res.push(betGrp);
        }

        res.sort((a, b) => { return a.amount - b.amount; });

        return res;
    }

    protected parseAllHits(betW: IBetWayWinData[], amountOfReels: number, reelHeights: number | number[]): IAllWinData {
        let allWinData: IAllWinData = {
            symbols: [],
            longest: 0,
            reelWinPositions: [],
            reelWinLvlPositions: [],
            totalWinCombination: 0,
            biggestWinLvl: 0,
            multiplier: 1,
        };

        for (let r: number = 0; r < amountOfReels; r++) {
            allWinData.reelWinPositions.push([])
            allWinData.reelWinLvlPositions.push([])
            for (let j: number = 0; j < ArrayHelper.fetchArrTypeValue(reelHeights, r); j++) {
                allWinData.reelWinPositions[r].push(false);
                allWinData.reelWinLvlPositions[r].push(-1);
            }
        }

        for (let groups = 0; groups < betW!.length; groups++) {
            const winData: IBetWayWinData = betW![groups];

            allWinData.symbols.push(winData.symbol);
            allWinData.totalWinCombination += winData.numberOfCombinations;
            allWinData.multiplier = winData.multiplier;
            if (winData.length > allWinData.longest) allWinData.longest = winData.length;

            for (let reel = 0; reel < winData.reelWinPositions.length; reel++) {
                let posRow: boolean[] = winData.reelWinPositions[reel];
                for (let row = 0; row < posRow.length; row++) {
                    if (posRow[row]) {
                        allWinData.reelWinPositions[reel][row] = posRow[row];
                        allWinData.reelWinLvlPositions[reel][row] = winData.reelWinLvlPositions[reel][row];
                        if (allWinData.biggestWinLvl < winData.reelWinLvlPositions[reel][row]) allWinData.biggestWinLvl = winData.reelWinLvlPositions[reel][row];
                    }
                }
            }
        }

        return allWinData;
    }

    protected createConnectedWinWays(data: IBetWayWinData[], reels: string[][]): IBetWayWinData[] {

        const gen: IConnectedWin[] = [];

        // const testvalue: string[][] = [
        //     ["W", "L3", "L4", "L4", "W"],
        //     ["L5", "W", "L4", "M2", "W"],
        //     ["L5", "L3", "L4", "L4", "W"],
        //     ["L4", "L3", "L4", "M5", "W"],
        //     ["L2", "L2", "L2", "L2", "L2"]
        // ];

        gen.push(...this.genConnectedByRes(data, reels, [BasicSymbolType.WILD, "WC1", "WC2", "WC3", "WC12"]))
        const res: IBetWayWinData[] = this.applyWinDataForConnected(data, gen);
        return res;
    }

    protected applyWinDataForConnected(data: IBetWayWinData[], gen: IConnectedWin[]): IBetWayWinData[] {
        const completedArr: IBetWayWinData[] = [];

        data.forEach(d => {
            const fetched: IConnectedWin | undefined = gen.find(g => g.symbol === d.symbol && g.groups[d.length]);

            if (fetched) {
                const dataToFill: IBetWayWinData = createNewBlock(fetched.symbol);

                dataToFill.amount = d.amount;
                dataToFill.length = d.length;
                dataToFill.numberOfCombinations = d.numberOfCombinations;
                dataToFill.multipliedAmount = d.multipliedAmount;
                dataToFill.multiplier = d.multiplier;
                dataToFill.reelWinPositions = convToBooleanMap(fetched.groups[d.length])
                completedArr.push(dataToFill);
            }
        });


        function convToBooleanMap(arr: number[][]): boolean[][] {
            return ArrayHelper.initArrayWithValues(arr.length, (i) => ArrayHelper.initArrayWithValues(arr[i].length, (k) => arr[i][k] != -1))
        }


        function createNewBlock(type: string): IBetWayWinData {
            return {
                symbol: type,
                amount: 0,
                length: 0,
                winLvl: 0,
                multiplier: 0,
                multipliedAmount: 0,
                numberOfCombinations: 0,
                reelWinPositions: [],
                reelWinLvlPositions: []
            };
        }

        return completedArr;
    }

    protected aiTest() {
        type Grid = string[][];
        type Symbols = string[];
        // Example Usage
        const grid: string[][] = [
            ['A', 'B', 'A', 'A', 'C'],
            ['A', 'A', 'C', 'D', 'A'],
            ['C', 'A', 'A', 'A', 'A'],
            ['D', 'C', 'A', 'B', 'C'],
            ['A', 'A', 'A', 'C', 'B']
        ];

        const specialSymbols: string[] = ['A', 'C'];
        const includeSpecial: boolean = true;

        const result: string[][] = this.findWinConnections(grid, specialSymbols, includeSpecial);
        console.log('Possible Win Connections:', result);

    }

    protected findWinConnections(grid: string[][], specialSymbols: string[], includeSpecial: boolean): string[][] {
        const rows: number = grid.length;
        const cols: number = grid[0].length;
        const winConnections: string[][] = [];

        // Helper function to validate if a symbol matches
        const isValidSymbol = (symbol: string, target: string): boolean => {
            return (
                target === symbol || (includeSpecial && (specialSymbols.includes(symbol) || specialSymbols.includes(target)))
            );
        };

        // Directions for checking (horizontal, vertical, diagonal-down, diagonal-up)
        const directions = [
            // { dr: 0, dc: 1 }, // Horizontal
            { dr: 1, dc: 0 }, // Vertical
            { dr: 1, dc: 1 }, // Diagonal Down
            { dr: 1, dc: -1 } // Diagonal Up
        ];

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const symbol: string = grid[r][c];
                if (symbol === '') continue; // Skip empty cells

                for (const { dr, dc } of directions) {
                    const connection: string[] = [`${r},${c}`];
                    let valid = true;

                    for (let k = 1; k < 5; k++) { // Check for up to 5 connected symbols
                        const nr: number = r + dr * k;
                        const nc: number = c + dc * k;

                        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) {
                            valid = false;
                            break; // Out of bounds
                        }

                        const nextSymbol: string = grid[nr][nc];
                        if (!isValidSymbol(symbol, nextSymbol)) {
                            valid = false;
                            break; // Not matching or invalid symbol
                        }
                        connection.push(`${nr},${nc}`);
                    }

                    if (valid) {
                        winConnections.push(connection);
                    }
                }
            }
        }

        return winConnections;
    }

    protected genConnectedByRes(winGrps:IBetWayWinData[], windowReel: string[][], specialSymbols: string[]) {
        const res: IConnectedWin[] = [];
        const minWinLength: number = 3;

        const directionsLeft = [
            // { x: 0, y: -1 }, // Up
            // { x: 0, y: 1 }, // Down
            { x: -1, y: 0 }, // Horizontal - Left
            { x: -1, y: 1 }, // Diagonal Down - Left
            { x: -1, y: -1 }, // Diagonal Up - Left
        ];

        const directionsRight = [
            // { x: 0, y: -1 }, // Up
            // { x: 0, y: 1 }, // Down
            { x: 1, y: 0 }, // Horizontal - Right
            { x: 1, y: 1 }, // Diagonal Down - Right
            { x: 1, y: -1 } // Diagonal Up - Right
        ];

        winGrps.forEach((grp,i) => {
            const grpHitMap: boolean[][] = ArrayHelper.initArrayWithValues(windowReel.length, (i) => ArrayHelper.initArrayWithValues(windowReel[i].length, () => false));
            const hitAbleSyms = specialSymbols.concat(grp.symbol);
            const symPos:{reel:number, row:number}[] = [];
            const resFetch = symbolDataFetch(grp.symbol);
            
            ArrayHelper.eachForEach(windowReel,(rl,rw,sym) => {
                if(grp.symbol === sym) symPos.push({reel:rl,row:rw});
            })

            symPos.forEach(pos => {
                if(grpHitMap[pos.reel][pos.row]) return; // Already ok

                const posCollection:{reel:number, row:number}[] = [];

                deepCheck(pos, posCollection, hitAbleSyms, directionsLeft);
                deepCheck(pos, posCollection, hitAbleSyms, directionsRight);

                const rightReel = Math.max(...posCollection.map(v => v.reel));
                const leftReel = Math.min(...posCollection.map(v => v.reel));

                if(rightReel+1 >= minWinLength && leftReel == 0){
                    if(!resFetch.groups[rightReel+1]) resFetch.groups[rightReel+1] = ArrayHelper.initArrayWithValues(windowReel.length, (i) => ArrayHelper.initArrayWithValues(windowReel[i].length, () => -1));

                    posCollection.forEach(v => {
                        resFetch.groups[rightReel+1][v.reel][v.row] = v.reel;
                    })
                }
            })
        })

        // Clean length Missmatch.
        res.forEach(v => {
            Object.keys(v.groups).forEach(key => {
                const grpLen = parseFloat(key);
                v.groups[key].forEach((vr,reel) => {

                    if(Math.max(...vr) < grpLen-1){
                        vr.forEach((vrr,row) => {
                            if(vrr != -1){
                                let check = false;
                                for (const dir of directionsRight) {
                                    if(v.groups[key][reel+dir.x][row+dir.y] != undefined){
                                        // If the position find a connection, then continue.
                                        if(v.groups[key][reel+dir.x][row+dir.y] >= 0){
                                            check = true;
                                            break;
                                        } 
                                    }
                                }
                                if(!check){ // This position does not connect to the group length.
                                    v.groups[key][reel][row] = -1;
                                }
                            }
                        })
                    }
                })
            })
        })
        
        return res;

        function deepCheck(
            posCheck:{reel:number, row:number},
            symCollect:{reel:number, row:number}[],
            hitAbleSyms:string[],
            direction: {
                x: number;
                y: number;
            }[]
            ):{reel:number, row:number}[]{
            if(windowReel[posCheck.reel] && windowReel[posCheck.reel][posCheck.row]){

                if(hitAbleSyms.find(v => v === windowReel[posCheck.reel][posCheck.row])){
                    if(!symCollect.find(r => r.reel == posCheck.reel && r.row == posCheck.row))
                        symCollect.push(posCheck);
                }else return symCollect;

                for (const dir of direction) {
                    deepCheck({reel:dir.x+posCheck.reel, row:dir.y+posCheck.row}, symCollect, hitAbleSyms, direction)
                }
            }
            return symCollect;
        }

        function symbolDataFetch(symTarget:string){
            let val = res.find(v => v.symbol === symTarget);
            if(!val){
                res.push({symbol:symTarget, groups:{}})
                val = ArrayHelper.fetchLastValue(res);
            }
            return val;
        }
    }

    protected simpleConnectedWinWays(windowReel: string[][], specialSymbols: string[]) {
        const res: IConnectedWinBool[] = [];
        const minWinLength: number = 2;
        const maxLength: number = windowReel.length;

        // let firstSymbols: string[][] = ArrayHelper.initArrayWithValues(windowReel[0].length, (i) => [windowReel[0][i]]);
        const directions = [
            // { x: 0, y: -1 }, // Up
            // { x: 0, y: 1 }, // Down
            { x: -1, y: 0 }, // Horizontal
            { x: -1, y: 1 }, // Diagonal Down
            { x: -1, y: -1 } // Diagonal Up
        ];



        for (let reel: number = windowReel.length-1; minWinLength <= reel; reel--) {
            const reelMap = windowReel[reel];
            for(let row: number = 0; row < reelMap.length;row++){
                let symStart = reelMap[row];
                const hitAbleSyms = specialSymbols.concat(symStart);
                const resFetch = symbolDataFetch(symStart);
                const collumnTest: boolean[][] = ArrayHelper.initArrayWithValues(windowReel.length, (i) => ArrayHelper.initArrayWithValues(windowReel[i].length, () => false));

                let connected:boolean = true;
                let isSpecial:boolean = !!specialSymbols.find(v => v === symStart);

                if(isSpecial){

                }

                for (let reelCheck: number = reel; 0 <= reelCheck; reelCheck--){
                    for (const dir of directions) {
                        const symCheck = windowReel[reelCheck][row + dir.y];
                        if(!symCheck) continue; // Remove undefined;
                        collumnTest[reelCheck][row + dir.y] = checker(symCheck, hitAbleSyms);
                    }
                    if(!collumnTest[reelCheck].find(v => v)){
                        connected = false;
                        break;
                    }
                }

                if(connected){
                    if(!resFetch.groups[reel+1]) resFetch.groups[reel+1] = ArrayHelper.initArrayWithValues(windowReel.length, (i) => ArrayHelper.initArrayWithValues(windowReel[i].length, () => false));

                    ArrayHelper.eachForEach(collumnTest,(re,ro,val) =>{
                        if(val) resFetch.groups[reel+1][re][ro] = val;
                    })
                }
            }
        }
        res;
        debugger;

        function symbolDataFetch(symTarget:string){
            let val = res.find(v => v.symbol === symTarget);
            if(!val){
                res.push({symbol:symTarget, groups:{}})
                val = ArrayHelper.fetchLastValue(res);
            }
            return val;
        }

        // Directions for checking (horizontal, vertical, diagonal-down, diagonal-up)
        function mergeWeightedPos(currWinData: IConnectedWin, array: number[][], lengthValue: number) {

            const filtArr: number[][] = array.filter((v) => v.indexOf(lengthValue) >= 0);
            if (!filtArr.length) return;

            if (!currWinData.groups[lengthValue]) currWinData.groups[lengthValue] = [];

            currWinData.groups[lengthValue] = currWinData.groups[lengthValue].concat(filtArr);

            const template: number[][] = ArrayHelper.initArrayWithValues(lengthValue, () => ArrayHelper.initArrayWithValues(array[0].length, () => 0));

            ArrayHelper.eachForEach(currWinData.groups[lengthValue], (reel, row, v) => {
                if (v) template[reel % lengthValue][row] = v;
            })
            currWinData.groups[lengthValue] = template;
        }

        function weightConnection(prevConnected: number[], path: number[], highValue: number, reelValue: number): number[] {

            let minorMap: number[] = ArrayHelper.initArrayWithValues(prevConnected.length, () => 0);

            prevConnected.forEach((v, i) => {

                if (v) {
                    if (highValue === reelValue && reelValue < v) return;
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = highValue > path[checkIndx] && path[checkIndx] ? highValue : 0;
                    })
                }
            })

            return minorMap;
        }

        function onlyUnique(value: any, index: any, self: any) {
            return self.indexOf(value) === index;
        }

        function checkConnected(prevConnected: boolean[], reel: string[], symbolType: string[]): boolean[] {
            let minorMap: boolean[] = ArrayHelper.initArrayWithValues(prevConnected.length, () => false);
            prevConnected.forEach((v, i) => {
                if (v) {
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = checker(reel[checkIndx], symbolType);
                    })
                }
            })
            return minorMap;
        }

        function connectCheck(startIndx: number, maxHeight: number, func: (indx: number) => void): void {
            const startPos: number = Math.max(0, startIndx - 1); // Forward-Up

            const endPos: number = Math.min(maxHeight - 1, startIndx + 1); // Forward-Down

            for (let checkIndx: number = startPos; checkIndx <= endPos; checkIndx++) func(checkIndx)
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find(v => v === symbol);
        }
    }

    protected createBetWaysWins(reels: string[][], serverBetWindata: IBetWayWinData[], fromLeft: boolean = true, fromRight: boolean = false): IBetWayWinData[] {

        const res: IBetWayWinData[] = [];

        if (!serverBetWindata.length) return res;

        if (fromLeft) res.push(...this.generateBetWinWays(serverBetWindata, reels))
        if (fromRight) res.push(...this.generateBetWinWays(serverBetWindata, reels.reverse()).reverse())

        return res;
    }

    protected generateBetWinWays(serverBetWindata: IBetWayWinData[], windowReel: string[][]): IBetWayWinData[] {
        const res: IBetWayWinData[] = [];

        for (let i: number = 0; i < serverBetWindata.length; i++) {
            const serverData: IBetWayWinData = serverBetWindata[i];
            const targetSymbol: string = serverData.symbol;
            const reelPos: boolean[][] = ArrayHelper.initArrayWithValues(windowReel.length, (r) => {
                if (serverData.length > r)
                    return reelHitBoolPos(windowReel[r], [targetSymbol, BasicSymbolType.WILD, "WC1", "WC2", "WC3", "WC12"]);
                else
                    return ArrayHelper.initArrayWithValues(windowReel[r].length, () => false);
            });

            res.push({
                symbol: targetSymbol,

                length: serverData.length,
                reelWinPositions: reelPos,

                amount: serverData.amount,
                numberOfCombinations: serverData.numberOfCombinations,

                multiplier: serverData.multiplier,
                multipliedAmount: serverData.multipliedAmount,

                winLvl: undefined!,
                reelWinLvlPositions: undefined!,
            })
        }

        function reelHitBoolPos(reel: string[], symbolType: string[]): boolean[] {
            return reel.map((val: string) => {
                return checker(val, symbolType);
            });
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find(v => v === symbol);
        }
        return res;
    }

    protected getWinRatioConfig(winRatio: number, allWinRatios: IWinRatioConfig[]): IWinRatioConfig {

        for (let i: number = allWinRatios.length - 1; i >= 0; i--) {
            const winLevel = allWinRatios[i];
            if (winRatio >= winLevel.ratio) {
                return winLevel;
            }
        }
        throw "Could'nt find WinRatio config!";
    }
}